/**
 * OI Breakdown Chart
 * Horizontal bar chart showing OI per exchange
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from './loading-skeleton';
import { useCoinglassOIBreakdown } from '@/hooks/use-coinglass';
import { formatCompact } from '@/lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { Asset } from '@/stores/alert-store';

interface OIBreakdownChartProps {
  asset: Asset;
}

const EXCHANGE_COLORS: Record<string, string> = {
  binance: '#F0B90B',
  okx: '#FFFFFF',
  bybit: '#F7A600',
  deribit: '#00C853',
  bitget: '#00D4AA',
  gate: '#2354E6',
  htx: '#1A68FF',
  aggregated: '#6366f1',
};

function getExchangeColor(exchange: string): string {
  return EXCHANGE_COLORS[exchange.toLowerCase()] || '#8884d8';
}

export function OIBreakdownChart({ asset }: OIBreakdownChartProps) {
  const { data, isLoading } = useCoinglassOIBreakdown(asset);

  if (isLoading) return <LoadingSkeleton className="h-64" lines={5} />;
  if (!data?.oi) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">OI Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No data</p>
        </CardContent>
      </Card>
    );
  }

  const chartData = Object.entries(data.oi)
    .filter(([exchange]) => exchange !== 'aggregated')
    .map(([exchange, entry]) => ({
      exchange: exchange.charAt(0).toUpperCase() + exchange.slice(1),
      exchangeKey: exchange,
      oi: entry.value,
      change: entry.change24h,
    }))
    .sort((a, b) => b.oi - a.oi)
    .slice(0, 8);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">OI Breakdown</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 60, right: 10 }}>
            <XAxis
              type="number"
              tickFormatter={(v: number) => `$${formatCompact(v)}`}
              tick={{ fontSize: 10, fill: '#9ca3af' }}
            />
            <YAxis
              type="category"
              dataKey="exchange"
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              width={55}
            />
            <Tooltip
              formatter={(value: number) => [`$${formatCompact(value)}`, 'OI']}
              contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '6px' }}
              labelStyle={{ color: '#d1d5db' }}
              itemStyle={{ color: '#d1d5db' }}
            />
            <Bar dataKey="oi" radius={[0, 4, 4, 0]}>
              {chartData.map((entry) => (
                <Cell key={entry.exchangeKey} fill={getExchangeColor(entry.exchangeKey)} fillOpacity={0.8} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
